import { SrSalesOrderType } from '@plato/data/starrocks/types';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DateText } from '../../../components/common/date-time-text.tsx';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { DataText } from '../../../components/typography/data-text.tsx';
import { QuantityText } from '../../../components/typography/quantity-text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { GetManyQuotesOrdersListContaingProductListItem } from '../../../server/types/infer-trpc-types.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { useCustomerMode } from '../../customer-mode/hooks/use-customer-mode.tsx';
import { OrderLink } from '../../quote-order-detail/components/quote-order-link.tsx';
import { FromNow } from '../../shared/components/from-now/from-now.tsx';
import { OrderStatusBadge } from '../../statuses/components/order-status-badge.tsx';
import { DoubleDeckerCell } from '../components/double-decker-cell.tsx';
import { IdAndNameCell } from '../components/id-and-name-cell.tsx';
import { MarginPercentage } from '../components/margin-percentage.tsx';

export const useOrderColumnsForProductDetail = () => {
    const { t } = useLocale();

    const { canReadGrossMargin } = useAppConfig();
    const { customerViewToggled } = useCustomerMode();

    const orderColumns: ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[] = useMemo(
        () => [
            {
                accessorKey: 'customer_name',
                header: t('table_header__customer_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row, cell }) => (
                    <IdAndNameCell
                        to={`/customers/${row.original.customer_id}`}
                        id={row.original.customer_id}
                        title={cell.renderValue() as string}
                    />
                ),
                size: 250,
            },
            {
                accessorKey: 'order_id',
                header: t('table_header__order_id', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => <OrderLink transactionId={row.original.order_id} />,
            },
            {
                accessorKey: 'status',
                header: t('table_header__status', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <OrderStatusBadge status={row.original.status} type={SrSalesOrderType.ORDER} />
                ),
            },
            {
                accessorKey: 'entry_date',
                header: t('table_header__order_created', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DoubleDeckerCell
                        align="left"
                        topClassName="text-neutral-500"
                        topLevel={<FromNow>{row.original.entry_date}</FromNow>}
                        bottomLevel={<DateText date={row.original.entry_date} />}
                    />
                ),
            },
            {
                accessorKey: 'item_quantity_in_order',
                header: t('table_header__quantity', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <QuantityText
                            quantity={row.original.item_quantity_in_order}
                            unit={row.original.quantity_unit_sales}
                        />
                    </DataText>
                ),
            },

            {
                accessorKey: 'product_unit_price',
                header: t('table_header__price', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.product_unit_price}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'product_total_in_order',
                header: t('table_header__total', {
                    ns: 'quotes-orders-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.product_total_in_order}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            ...(customerViewToggled || !canReadGrossMargin
                ? []
                : ([
                      {
                          accessorKey: 'product_gross_margin_in_order',
                          header: t('table_header__margin', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) => (
                              <DataText>
                                  <CurrencyOrEmpty decimals={2}>
                                      {row.original.product_gross_margin_in_order}
                                  </CurrencyOrEmpty>
                              </DataText>
                          ),
                      },
                      {
                          accessorKey: 'product_gross_margin_percent',
                          header: t('table_header__margin_percent', {
                              ns: 'quotes-orders-list',
                          }),
                          cell: ({ row }) => (
                              <DataText>
                                  <MarginPercentage
                                      margin={row.original.product_gross_margin_in_order}
                                      revenue={row.original.product_total_in_order}
                                  />
                              </DataText>
                          ),
                      },
                  ] as ColumnDef<GetManyQuotesOrdersListContaingProductListItem>[])),
        ],
        [t, canReadGrossMargin, customerViewToggled],
    );

    return orderColumns;
};
